/**
 * 「よく使われる事例」のタグ絞り込み UI。
 *
 * ## なぜ検索欄ではなくタグから作るか
 *
 * 利用者は「駅名が聞き取りにくい」「方面のアクセントが変」といった
 * **語彙が定まらない困りごと**を持って来る。自由入力の検索は
 * 語が一致しないと 0 件になるため、まずは押すだけで絞れるタグを入口にする。
 * (文字列検索は将来追加する。絞り込み判定は `useCaseFilter.ts` に
 *  集約してあるため、入力欄を足すだけで済む構造にしてある)
 *
 * ## 設計上の約束
 *
 * - **データは props で受ける** (`useCaseTags.ts` / `useCases.ts` を import しない)。
 * - 選択状態は親が保持する。ここは「押された」ことを伝えるだけ。
 * - タグは複数選択でき、判定は **OR** (`useCaseFilter.ts` 側の仕様)。
 *   `aria-pressed` を持つトグルボタンとして表現する
 *   (タブではないので `role="tab"` は使わない)。
 */
import type React from 'react';

import type { SsmlUseCaseTag, SsmlUseCaseTagDefinition } from './useCaseTags';

export interface UseCaseTagFilterProps {
  /** 表示するタグ (配列順が表示順。件数 0 のタグは親側で除外して渡す) */
  tags: readonly SsmlUseCaseTagDefinition[];
  /** 選択中のタグ (空 = 絞り込みなし) */
  selectedTags: readonly SsmlUseCaseTag[];
  /** タグごとの件数 (バッジ表示用)。未指定のタグは件数を出さない */
  tagCounts?: ReadonlyMap<SsmlUseCaseTag, number>;
  /** タグが押されたときの通知 (トグルの計算は親 or useCaseFilter 側) */
  onToggleTag: (tag: SsmlUseCaseTag) => void;
  /** 「すべて表示」が押されたときの通知 (選択解除) */
  onClearTags: () => void;
}

export function UseCaseTagFilter({
  tags,
  selectedTags,
  tagCounts,
  onToggleTag,
  onClearTags,
}: UseCaseTagFilterProps): React.JSX.Element {
  const hasSelection = selectedTags.length > 0;

  return (
    <div className="ssml-usecase-filter" data-testid="ssml-usecase-filter">
      <p className="ssml-usecase-filter__label" id="ssml-usecase-filter-label">
        タグで絞り込む
      </p>
      <div
        className="ssml-usecase-filter__tags"
        role="group"
        aria-labelledby="ssml-usecase-filter-label"
      >
        {/*
          「すべて表示」は絞り込み解除の明示的な出口。
          タグを再度押して外す操作に気づけない利用者のために必ず置く。
        */}
        <button
          type="button"
          className={`ssml-usecase-tag${hasSelection ? '' : ' is-active'}`}
          aria-pressed={!hasSelection}
          data-testid="ssml-usecase-tag-all"
          onClick={onClearTags}
        >
          すべて表示
        </button>

        {tags.map((tag) => {
          const isSelected = selectedTags.includes(tag.tag);
          const count = tagCounts?.get(tag.tag);
          return (
            <button
              key={tag.tag}
              type="button"
              className={`ssml-usecase-tag${isSelected ? ' is-active' : ''}`}
              aria-pressed={isSelected}
              // タグの意味を hover で補える (ラベルだけでは範囲が分かりにくいため)
              title={tag.description}
              data-testid={`ssml-usecase-tag-${tag.tag}`}
              onClick={() => onToggleTag(tag.tag)}
            >
              {tag.label}
              {count === undefined ? null : (
                <span className="ssml-usecase-tag__count">{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
